import { clearAuthTokens, isLoggedIn } from 'axios-jwt';
import { defineStore } from 'pinia';
import cloneDeep from 'lodash.clonedeep';
import { EMPTY_USER, type User } from '@/models/User';
import gettext from '@/i18n/gettext';
import { LOGOUT_URL } from '@/Constants';
import { getUserPermissions, loadUserProfile } from '@/api/UserApi';
import type { Permission } from '@/models/Roles';

const useAuthStore = defineStore('auth', {
  state: () => ({
    isAuthenticated: false,
    user: cloneDeep(EMPTY_USER),
    permissions: [] as Permission[],
    role: null as string | null,
    redirectRoute: '/',
    permissionsLoaded: false,
    permissionsLoading: false,
    permissionsPromise: null as Promise<void> | null,
    termsAccepted: false,
    bootPromise: null as Promise<void> | null,
    bootCompleted: false,
    profileLoaded: false,
    profilePromise: null as Promise<void> | null,
  }),
  getters: {
    language: () => gettext.current,
    getUserFullName: (state) => {
      if (state.user.name) {
        return state.user.name;
      }
      return '';
    },
  },
  actions: {
    toggleLanguage() {
      if (this.language === 'de') {
        this.setLanguage('en');
      } else {
        this.setLanguage('de');
      }
    },
    /**
     * Sets the language in gettext, localstorage and user object
     * @param lang The language to set
     */
    setLanguage(lang: string) {
      gettext.current = lang;
      localStorage.setItem('currentLanguage', lang);
      this.user.language = lang;
    },
    /**
     * Loads the user object from localStorage to Vuex Store
     */
    async loadUser() {
      if (await isLoggedIn()) {
        this.isAuthenticated = true;
        const cachedUser = localStorage.getItem('user');
        if (cachedUser) {
          this.user = JSON.parse(cachedUser);
        }
        const cachedTerms = localStorage.getItem('termsAccepted');
        this.termsAccepted = cachedTerms === 'true';
      }
    },
    /**
     * Set the user in localStorage and Vuex Store
     * @param user The user object to save
     */
    async setUser(user: User) {
      if (await isLoggedIn()) {
        this.isAuthenticated = true;
        this.user = user;
        localStorage.setItem('user', JSON.stringify(user));
      }
    },
    setTermsAccepted(value: boolean) {
      this.termsAccepted = value;
      localStorage.setItem('termsAccepted', value ? 'true' : 'false');
    },
    setBootPromise(p: Promise<void> | null) {
      this.bootPromise = p;
      this.bootCompleted = false;
    },
    markBootCompleted() {
      this.bootCompleted = true;
      this.bootPromise = null;
    },
    /**
     * Reset cached user, permissions, and flags without redirecting.
     * Use this before starting a fresh login flow with an auth code.
     */
    resetUserData() {
      this.isAuthenticated = false;
      this.user.uuid = '';
      this.user.email = '';
      this.user.name = '';
      this.user.language = 'en';
      this.user.profilePic = '';
      localStorage.removeItem('user');
      localStorage.removeItem('termsAccepted');
      this.permissions = [];
      this.role = null;
      this.permissionsLoaded = false;
      this.permissionsLoading = false;
      this.permissionsPromise = null;
      this.termsAccepted = false;
      this.redirectRoute = '/';
    },
    markProfileLoaded() {
      this.profileLoaded = true;
      this.profilePromise = null;
    },
    /**
     * Loads the permissions object from localStorage to Vuex Store
     */
    async loadPermissions() {
      if (!this.isAuthenticated || !this.termsAccepted) {
        return;
      }
      if (this.permissionsLoaded) {
        return;
      }
      if (this.permissionsPromise) {
        return this.permissionsPromise;
      }

      this.permissionsLoading = true;
      this.permissionsPromise = getUserPermissions()
        .then(() => {
          this.permissionsLoaded = true;
        })
        .catch(async () => {
          this.permissionsLoading = false;
          this.permissionsPromise = null;
        });

      return this.permissionsPromise;
    },
    /**
     * Load the user profile once and cache the result to avoid duplicate calls.
     */
    async loadProfileOnce() {
      if (!this.isAuthenticated || !this.termsAccepted) {
        return;
      }
      if (this.profileLoaded) {
        return;
      }
      if (this.profilePromise) {
        return this.profilePromise;
      }
      this.profilePromise = loadUserProfile()
        .then(() => {
          this.markProfileLoaded();
        })
        .catch((error) => {
          this.profilePromise = null;
          throw error;
        });
      return this.profilePromise;
    },
    /**
     * Set the user permissions and role in the store
     * @param permissions The permissions array to save
     * @param role The user's role
     */
    setPermissions(permissions: Permission[], role: string | null = null) {
      if (this.isAuthenticated) {
        this.permissions = permissions;
        this.role = role;
        this.permissionsLoaded = true;
      }
    },
    hasPermission(permission: Permission) {
      return this.permissions.includes(permission);
    },
    /**
     * Wait until permissions have been loaded without initiating the load.
     * @param timeout The timeout in milliseconds to wait for the permissions to be loaded (default is 10 seconds)
     */
    waitForPermissions(timeout = 10000): Promise<void> {
      if (this.permissionsLoaded) {
        return Promise.resolve();
      }

      const start = Date.now();
      const checkIntervalMs = 10;

      return new Promise<void>((resolve, reject) => {
        const check = () => {
          if (this.permissionsLoaded) {
            resolve();
          } else if (Date.now() - start > timeout) {
            reject(new Error('Timeout waiting for permissions'));
          } else {
            setTimeout(check, checkIntervalMs);
          }
        };
        check();
      });
    },
    /**
     * Set a redirect route to be routed to after a successful login.
     * @param route The redirect route to be routed to after a successful login
     */
    setRedirectRoute(route: string) {
      this.redirectRoute = route;
    },

    /**
     * Logout and clear user data (in localStorage and in Vuex Store)
     * @param redirect: boolean to indicate whether to redirect to Cognito Login Page
     */
    async logoutAndClearUserData() {
      this.resetUserData();
      await clearAuthTokens();
      window.location.replace(LOGOUT_URL);
    },
  },
});

export default useAuthStore;
